import { PERMISSIONS } from '@nks/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Descriptions, Flex, List, Skeleton, Space, Tag, Timeline, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import { canResolve, getCase, isOpenCase, statusMoves, type ServiceCaseDetail } from './api';
import {
  NoteDrawer,
  ReassignDrawer,
  ResolveDrawer,
  ResponseDrawer,
  StatusDrawer,
  WhatsAppDrawer,
} from './CaseActionDrawers';
import { categoryLabel, channelLabel, severityColour, severityLabel, statusColour, statusLabel } from './labels';

const { Text } = Typography;

type Action = 'note' | 'response' | 'whatsapp' | 'status' | 'resolve' | 'reassign';

/** Where the linked document opens. MEMBERSHIP has no screen yet, so it is plain text. */
function linkHref(link: NonNullable<ServiceCaseDetail['link']>): string | null {
  switch (link.type) {
    case 'JOB_CARD':
      return `/workshop/job-cards/${link.uid}`;
    case 'SALE':
      return `/bookings/${link.uid}`;
    case 'DELIVERY':
      return `/deliveries/${link.uid}`;
    default:
      return null;
  }
}

export function CasePage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();
  const canManage = useHasPermission(PERMISSIONS.CASE_MANAGE);
  const canAssign = useHasPermission(PERMISSIONS.CASE_ASSIGN);
  const [action, setAction] = useState<Action | null>(null);

  const { data, error, isLoading } = useQuery({ queryKey: ['cases', 'one', uid], queryFn: () => getCase(uid) });

  if (error !== null && data === undefined) {
    return (
      <Card>
        <Flex vertical gap={12} align="flex-start">
          <Text type="danger">
            {error instanceof ApiError ? error.message : t('serviceDesk.caseLoadFailed')}
          </Text>
          <Button
            onClick={() => {
              void navigate('/cases');
            }}
          >
            {t('serviceDesk.backToList')}
          </Button>
        </Flex>
      </Card>
    );
  }
  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  const done = (successKey?: string): void => {
    setAction(null);
    void queryClient.invalidateQueries({ queryKey: ['cases'] });
    void message.success(t(successKey ?? 'serviceDesk.drawer.saved'));
  };
  const close = (): void => {
    setAction(null);
  };
  const drawerProps = { open: true, serviceCase: data, onClose: close };
  const open = isOpenCase(data.status);
  const mayChange = canManage && data.status !== 'CLOSED';

  const clock = (label: string, due: string, overdue: boolean, overdueLabel: string, stoppedAt: string | null, stoppedLabel: string): ReactNode => (
    <Flex vertical gap={2}>
      <Text type="secondary">{label}</Text>
      <Text strong type={overdue ? 'danger' : undefined}>
        {formatDateTime(due)}
      </Text>
      {stoppedAt !== null ? (
        <Text type="secondary">{stoppedLabel}</Text>
      ) : overdue ? (
        <Tag color="error">{overdueLabel}</Tag>
      ) : null}
    </Flex>
  );

  const link = data.link;
  const href = link !== null ? linkHref(link) : null;

  return (
    <>
      <DetailPage
        title={data.subject}
        subtitle={data.caseNo}
        tags={[
          { label: statusLabel(t, data.status), colour: statusColour(data.status) },
          { label: severityLabel(t, data.severity), colour: severityColour(data.severity) },
          { label: data.owner.name },
          ...(data.breached.response || data.breached.resolve
            ? [{ label: t('serviceDesk.overdue'), colour: 'error' }]
            : []),
        ]}
        onBack={() => {
          void navigate('/cases');
        }}
        actions={
          mayChange || canAssign ? (
            <Space wrap>
              {mayChange && (
                <>
                  <Button size="large" onClick={() => { setAction('note'); }}>{t('serviceDesk.actions.note')}</Button>
                  <Button size="large" onClick={() => { setAction('response'); }}>{t('serviceDesk.actions.response')}</Button>
                  <Button size="large" onClick={() => { setAction('whatsapp'); }}>{t('serviceDesk.actions.whatsapp')}</Button>
                  {statusMoves(data.status).length > 0 && (
                    <Button size="large" onClick={() => { setAction('status'); }}>{t('serviceDesk.actions.status')}</Button>
                  )}
                  {canResolve(data.status) && (
                    <Button size="large" type="primary" onClick={() => { setAction('resolve'); }}>
                      {t('serviceDesk.actions.resolve')}
                    </Button>
                  )}
                </>
              )}
              {canAssign && data.status !== 'CLOSED' && (
                <Button size="large" onClick={() => { setAction('reassign'); }}>{t('serviceDesk.actions.reassign')}</Button>
              )}
            </Space>
          ) : undefined
        }
        tabs={[
          {
            key: 'overview',
            label: t('serviceDesk.tabs.overview'),
            content: (
              <Flex vertical gap={16}>
                <Card size="small">
                  <Flex gap={32} wrap>
                    {clock(
                      t('serviceDesk.clock.reply'),
                      data.slaResponseDueAt,
                      data.breached.response,
                      t('serviceDesk.clock.replyOverdue'),
                      data.firstResponseAt,
                      t('serviceDesk.clock.replied', { at: formatDateTime(data.firstResponseAt) }),
                    )}
                    {clock(
                      t('serviceDesk.clock.resolve'),
                      data.slaResolveDueAt,
                      data.breached.resolve,
                      t('serviceDesk.clock.resolveOverdue'),
                      data.resolvedAt,
                      t('serviceDesk.clock.resolved', { at: formatDateTime(data.resolvedAt) }),
                    )}
                  </Flex>
                </Card>
                <Card>
                  <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                    <Descriptions.Item label={t('serviceDesk.field.customer')}>
                      <Link to={`/customers/${data.customer.uid}`}>{data.customer.name}</Link>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.phone')}>{data.customer.phone}</Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.category')}>{categoryLabel(t, data.category)}</Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.channel')}>{channelLabel(t, data.channel)}</Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.link')}>
                      {link === null ? (
                        orDash(null)
                      ) : href !== null ? (
                        <Link to={href}>{link.ref}</Link>
                      ) : (
                        link.ref
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.unit')}>
                      {data.unit === null ? orDash(null) : (
                        <Link to={`/units/${data.unit.uid}`}>{data.unit.plateNo ?? data.unit.chassisNo}</Link>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.opened')}>{formatDateTime(data.createdAt)}</Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.owner')}>{data.owner.name}</Descriptions.Item>
                    <Descriptions.Item label={t('serviceDesk.field.description')} span={2}>
                      {orDash(data.description)}
                    </Descriptions.Item>
                    {data.resolution !== null && (
                      <Descriptions.Item label={t('serviceDesk.field.resolution')} span={2}>
                        {data.resolution}
                      </Descriptions.Item>
                    )}
                    {data.satisfactionScore !== null && (
                      <Descriptions.Item label={t('serviceDesk.field.score')}>{data.satisfactionScore} / 5</Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Flex>
            ),
          },
          {
            key: 'activity',
            label: t('serviceDesk.tabs.activity'),
            content: (
              <Card>
                <List
                  locale={{ emptyText: t('serviceDesk.activity.empty') }}
                  dataSource={data.activities}
                  rowKey={(a) => a.uid}
                  renderItem={(a) => (
                    <List.Item>
                      <Flex vertical gap={2} style={{ width: '100%' }}>
                        <Flex gap={8} align="center" wrap>
                          <Tag>{t(`serviceDesk.activityType.${a.type}`, { defaultValue: a.type })}</Tag>
                          <Text type="secondary">
                            {t('serviceDesk.activity.by', { who: orDash(a.createdBy), at: formatDateTime(a.createdAt) })}
                          </Text>
                        </Flex>
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{a.note}</Text>
                      </Flex>
                    </List.Item>
                  )}
                />
              </Card>
            ),
          },
          {
            key: 'history',
            label: t('serviceDesk.tabs.history'),
            content: (
              <Card>
                {data.history.length === 0 ? (
                  <Text type="secondary">{t('serviceDesk.historyEmpty')}</Text>
                ) : (
                  <Timeline
                    items={data.history.map((h) => ({
                      key: h.uid,
                      children: (
                        <Flex vertical>
                          <Text strong>
                            {h.fromStatus === null
                              ? t('serviceDesk.historyOpened', { to: statusLabel(t, h.toStatus) })
                              : t('serviceDesk.historyFromTo', {
                                  from: statusLabel(t, h.fromStatus),
                                  to: statusLabel(t, h.toStatus),
                                })}
                          </Text>
                          <Text type="secondary">
                            {orDash(h.changedBy)}, {formatDateTime(h.at)}
                          </Text>
                          {h.reason !== null && <Text>{h.reason}</Text>}
                        </Flex>
                      ),
                    }))}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      {action === 'note' && <NoteDrawer {...drawerProps} onDone={() => { done(); }} />}
      {action === 'response' && <ResponseDrawer {...drawerProps} onDone={() => { done(); }} />}
      {action === 'whatsapp' && <WhatsAppDrawer {...drawerProps} onDone={() => { done('serviceDesk.drawer.waLogged'); }} />}
      {action === 'status' && <StatusDrawer {...drawerProps} onDone={() => { done(); }} />}
      {action === 'resolve' && open && <ResolveDrawer {...drawerProps} onDone={() => { done(); }} />}
      {action === 'reassign' && <ReassignDrawer {...drawerProps} onDone={() => { done(); }} />}
    </>
  );
}
